/*
 * Corkys World — Apple Music preview player.
 *
 * Rebuilt for Palo Alto, not ported from Levitation's Volume build: that element
 * carries a MusicKit upgrade path, Volume selectors and Volume CSS variables,
 * none of which run here.
 *
 * NOW-BAR matches Levitation's anatomy (snippets/apple-music-player.liquid):
 *   .amp__now > .amp__pbtn (big transport) + .amp__nm2 (track) + .amp__sub
 *   ("artist · album") + .amp__prog with .amp__track > .amp__fill and two
 *   .amp__t time labels. Same parts, cvp- prefix.
 *
 * Data path (verified against the Corkys catalog):
 *   GET https://api.music.apple.com/v1/catalog/{storefront}/albums/{id}
 *   -> data[0].attributes{ name, artistName }
 *      data[0].relationships.tracks.data[].attributes{ name, previews[0].url,
 *      durationInMillis, trackNumber }
 *
 * Attributes: album-id, storefront, token, accent (optional).
 */
(function () {
  'use strict';
  if (customElements.get('corkys-music-player')) return;

  var API = 'https://api.music.apple.com/v1/catalog';

  // One <audio> per page: starting one player stops any other, which is what you
  // want when several formats of the same record are on screen.
  var shared = null;
  function audio() {
    if (!shared) { shared = new Audio(); shared.preload = 'none'; }
    return shared;
  }
  var active = null;

  function ms(sec) {
    var s = Math.max(0, Math.round(sec || 0));
    return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
  }
  function el(tag, cls, txt) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  }
  var PLAY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  var PAUSE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z"/></svg>';

  class CorkysMusicPlayer extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this.albumId = this.getAttribute('album-id');
      this.storefront = this.getAttribute('storefront') || 'us';
      this.token = this.getAttribute('token');
      if (this.getAttribute('accent')) this.style.setProperty('--cvp-accent', this.getAttribute('accent'));
      if (!this.albumId || !this.token) return this.fail('missing album id or token');

      // Lazy. An IntersectionObserver does not fire in a background tab, so a
      // headless check must call load() directly.
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (es) {
          es.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); e.target.load(); } });
        }, { rootMargin: '300px 0px' });
        io.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (active && active.player === this) {
        try { audio().pause(); } catch (e) {}
        active = null;
      }
    }

    fail(why) {
      this.setAttribute('data-cvp-failed', why || '1');
      var scope = this.closest('.cvp') || document;
      var fb = scope.querySelector('[data-cvp-tracklist-fallback]');
      if (fb) { fb.hidden = false; this.hidden = true; return; }
      // Nothing to hand back to: hide the whole panel rather than leave an empty
      // Listen tab. ~6,894 products have an album id but no stored tracklist.
      this.hidden = true;
      var host = this.closest('.cvp-pnl');
      if (host) host.hidden = true;
    }

    load() {
      if (this._loading || this._tracks) return;
      this._loading = true;
      var key = 'corkys-am2:' + this.storefront + ':' + this.albumId;
      var self = this, cached = null;
      try { cached = sessionStorage.getItem(key); } catch (e) {}
      if (cached) { try { return this.render(JSON.parse(cached)); } catch (e) {} }
      fetch(API + '/' + this.storefront + '/albums/' + encodeURIComponent(this.albumId), {
        headers: { Authorization: 'Bearer ' + this.token },
      })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (d) {
          var alb = (d.data || [])[0];
          if (!alb) throw new Error('no album');
          var at = alb.attributes || {};
          var tr = ((alb.relationships || {}).tracks || {}).data || [];
          var payload = {
            album: at.name || '',
            artist: at.artistName || '',
            t: tr.map(function (t) {
              var a = t.attributes || {};
              return {
                n: a.trackNumber, name: a.name, dur: a.durationInMillis,
                url: ((a.previews || [])[0] || {}).url || null,
              };
            }),
          };
          try { sessionStorage.setItem(key, JSON.stringify(payload)); } catch (e) {}
          self.render(payload);
        })
        .catch(function (e) { self.fail(String(e.message || e)); });
    }

    render(data) {
      var tracks = data.t || [];
      this._tracks = tracks;
      this._meta = { album: data.album || '', artist: data.artist || '' };
      this._loading = false;
      var playable = tracks.filter(function (t) { return t.url; });
      if (!playable.length) return this.fail('no previews');

      var self = this;
      this.innerHTML = '';

      // ---------- now-bar ----------
      var now = el('div', 'cvp-amp__now');
      var pbtn = el('button', 'cvp-amp__pbtn');
      pbtn.type = 'button';
      pbtn.setAttribute('aria-label', 'Play');
      pbtn.innerHTML = PLAY;
      pbtn.addEventListener('click', function () {
        var i = (active && active.player === self) ? active.index : self._firstPlayable();
        self.toggle(i);
      });
      this._pbtn = pbtn;

      var meta = el('div', 'cvp-amp__meta');
      this._nm = el('div', 'cvp-amp__nm', this._firstName());
      meta.appendChild(this._nm);
      var subText = [this._meta.artist, this._meta.album].filter(Boolean).join(' · ');
      if (subText) meta.appendChild(el('div', 'cvp-amp__sub', subText));

      var prog = el('div', 'cvp-amp__prog');
      this._cur = el('span', 'cvp-amp__t', '0:00');
      var bar = el('span', 'cvp-amp__track');
      this._fill = el('span', 'cvp-amp__fill');
      bar.appendChild(this._fill);
      this._durEl = el('span', 'cvp-amp__t', '0:30');
      prog.appendChild(this._cur); prog.appendChild(bar); prog.appendChild(this._durEl);
      meta.appendChild(prog);

      now.appendChild(pbtn); now.appendChild(meta);
      this.appendChild(now);

      // ---------- track list, in its own scroll region ----------
      var ol = el('ol', 'cvp-amp__list');
      tracks.forEach(function (t, i) {
        var li = el('li', 'cvp-amp__row' + (t.url ? '' : ' is-unavailable'));
        li.appendChild(el('span', 'cvp-amp__num', String(t.n || i + 1).padStart(2, '0')));
        li.appendChild(el('span', 'cvp-amp__name', t.name || ''));
        li.appendChild(el('span', 'cvp-amp__dur', t.dur ? ms(t.dur / 1000) : ''));
        if (t.url) {
          var b = el('button', 'cvp-amp__btn');
          b.type = 'button';
          b.setAttribute('aria-label', 'Play a preview of ' + (t.name || 'this track'));
          b.addEventListener('click', function () { self.toggle(i); });
          li.appendChild(b);
        }
        ol.appendChild(li);
      });
      var scroll = el('div', 'cvp-amp__scroll');
      scroll.appendChild(ol);
      this.appendChild(scroll);

      this.appendChild(el('p', 'cvp-amp__note',
        '30-second previews from Apple Music. Track listing may differ from the physical record.'));

      this.setAttribute('data-cvp-ready', playable.length);
      this.dispatchEvent(new CustomEvent('cvp:player-ready', { bubbles: true }));
    }

    _firstPlayable() {
      for (var i = 0; i < this._tracks.length; i++) if (this._tracks[i].url) return i;
      return 0;
    }
    _firstName() {
      var i = this._firstPlayable();
      return (this._tracks[i] && this._tracks[i].name) || this._meta.album || '';
    }

    toggle(i) {
      var t = this._tracks && this._tracks[i];
      if (!t || !t.url) return;
      var a = audio(), self = this;
      var same = active && active.player === this && active.index === i;
      // A finished track must rewind or play() resumes at the 30s end.
      if (same && a.ended) a.currentTime = 0;
      if (same && !a.paused) { a.pause(); return; }
      if (active && active.player !== this) active.player.mark(null);
      if (!same) a.src = t.url;

      a.ontimeupdate = function () {
        if (!active || active.player !== self) return;
        var d = a.duration || 30;
        if (self._fill) self._fill.style.width = Math.min(100, (a.currentTime / d) * 100) + '%';
        if (self._cur) self._cur.textContent = ms(a.currentTime);
        if (self._durEl) self._durEl.textContent = ms(d);
      };
      a.onended = function () { active = null; self.mark(null); self._reset(); };
      a.onerror = function () { active = null; self.mark(null); self._reset(); };
      a.onpause = function () { if (active && active.player === self) self.mark(null, true); };

      a.play().then(function () {
        active = { player: self, index: i };
        self.mark(i);
      }).catch(function () { self.mark(null); });
    }

    _reset() {
      if (this._fill) this._fill.style.width = '0%';
      if (this._cur) this._cur.textContent = '0:00';
    }

    mark(i, keepName) {
      var playing = (i !== null && i !== undefined);
      this.querySelectorAll('.cvp-amp__row').forEach(function (r, idx) {
        r.classList.toggle('is-playing', idx === i);
      });
      if (this._pbtn) {
        this._pbtn.classList.toggle('is-playing', playing);
        this._pbtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
        this._pbtn.innerHTML = playing ? PAUSE : PLAY;
      }
      if (this._nm) {
        if (playing && this._tracks[i]) this._nm.textContent = this._tracks[i].name || '';
        else if (!keepName) this._nm.textContent = this._firstName();
      }
    }
  }

  customElements.define('corkys-music-player', CorkysMusicPlayer);
})();
